import { computed, readonly, ref, toRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TriggerEventForm, TriggerEventBody, TriggerEventEventType, TriggerEventEventTypeWithGroup } from '@/api/openapi';
import { TriggerEventFormApiService } from '@/api/user/resources/trigger_event_form';
import { TriggerEventTypeApiService } from '@/api/user/resources/trigger_event_type';
import { useWait } from '@/composable/vue-wait';

const useTriggerEvent = (needsInitialFetch = () => true) => {
  const i18n = useI18n();

  const eventTypes = ref<TriggerEventEventTypeWithGroup[]>([]);

  const groupedEventTypes = computed(() => {
    return eventTypes.value.reduce((acc, eventType) => {
      let group = acc.find((group) => group.key === eventType.group);
      if (!group) {
        group = {
          key: eventType.group,
          label: i18n.t(`trigger.eventGroups.${eventType.group}`),
          options: [],
        };
        acc.push(group);
      }
      group.options.push({
        value: eventType.eventType,
        name: i18n.t(`trigger.eventTypes.${eventType.eventType}`),
      });
      return acc;
    }, []);
  });

  const fetchEventTypes = async () => {
    const api = new TriggerEventTypeApiService();
    const { data } = await api.getTriggerEventTypes();
    eventTypes.value = data.triggerEventTypes;
  };

  const makeInitialTriggerEvent = (triggerEvent: TriggerEventBody): TriggerEventBody => {
    return {
      leadViewId: null,
      eventType: null,
      filterParams: {},
      eventDetail: {},
      ...structuredClone(toRaw(triggerEvent) || {}),
    };
  };

  if (needsInitialFetch()) {
    fetchEventTypes();
  }

  return {
    groupedEventTypes: readonly(groupedEventTypes),
    makeInitialTriggerEvent,
  };
};

const useTriggerEventForm = () => {
  const { doActionWithWait, wait } = useWait();

  const forms = ref<TriggerEventForm[]>([]);
  const fetchForms = async (eventType: TriggerEventEventType) => {
    await doActionWithWait('fetchTriggerEventFormsWait', async () => {
      const api = new TriggerEventFormApiService();
      const { data } = await api.getTriggerEventForms({
        request: {
          eventType,
        },
      });
      forms.value = data.triggerEventForms;
    });
  };
  
  const loading = computed(() => {
    return wait.is('fetchTriggerEventFormsWait');
  });

  return {
    loading: readonly(loading),
    forms,
    fetchForms,
  };
};

// カードの常時表示用に、eventType ごとの forms をページ単位でキャッシュする。
// eventTypesSource が返す型集合を監視し、未取得の型だけを取得する。
const useTriggerEventFormsCache = (
  eventTypesSource: () => (TriggerEventEventType | null | undefined)[],
) => {
  const cache = ref<Partial<Record<TriggerEventEventType, TriggerEventForm[]>>>({});
  const inflight = new Map<TriggerEventEventType, Promise<void>>();
  const inflightCount = ref(0);

  const ensure = (eventType: TriggerEventEventType) => {
    if (cache.value[eventType] || inflight.has(eventType)) return;
    const api = new TriggerEventFormApiService();
    inflightCount.value += 1;
    const promise = api
      .getTriggerEventForms({ request: { eventType } })
      .then(({ data }) => {
        cache.value[eventType] = data.triggerEventForms;
      })
      .catch(() => {
        // 失敗時はキャッシュしない。次回の変更検知で再取得される。
      })
      .finally(() => {
        inflight.delete(eventType);
        inflightCount.value -= 1;
      });
    inflight.set(eventType, promise);
  };

  watch(
    eventTypesSource,
    (eventTypes) => {
      eventTypes.forEach((eventType) => {
        if (eventType) ensure(eventType);
      });
    },
    { immediate: true, deep: true },
  );

  const formsFor = (eventType: TriggerEventEventType | null | undefined): TriggerEventForm[] => {
    if (!eventType) return [];
    return cache.value[eventType] ?? [];
  };

  const loading = computed(() => inflightCount.value > 0);

  return { formsFor, loading: readonly(loading) };
};

export {
  useTriggerEvent,
  useTriggerEventForm,
  useTriggerEventFormsCache,
};
